import "../components/ViewportGuard/ViewportGuard.css";
import "../features/settings/settings-ui.css";
import { useState } from "preact/hooks";
import { FileDropZone } from "../components/FileDropZone/FileDropZone.jsx";
import { IconTriangleAlert } from "../components/Icon/Icon.jsx";
import { Panel } from "../components/Panel/Panel.jsx";
import { StatusMessage } from "../components/StatusMessage/StatusMessage.jsx";
import { DestructiveActionButton } from "../components/DestructiveActionButton/DestructiveActionButton.jsx";
import { useAppShortcuts } from "../lib/shortcuts/useAppShortcuts.js";
import { localizeErrorMessage } from "../lib/localizeErrorMessage.js";
import { APP_LANGUAGE_ENGLISH } from "../lib/settings/appSettings.js";
import { purgeWalletWatchDb, restoreProfileBackup } from "../lib/storage/walletWatchDb.js";
import { createDatabaseErrorPageShortcuts } from "./databaseErrorPageModel.js";

function footerToneClass(type) {
  if (type === "success") {
    return "tone-success";
  }

  if (type === "warning") {
    return "tone-warning";
  }

  if (type === "error") {
    return "tone-error";
  }

  return "";
}

export function DatabaseErrorPage({
  activeLanguage = APP_LANGUAGE_ENGLISH,
  error: databaseError = null,
  handleLanguageChange = null,
  t = (key) => key
}) {
  const [restoreStatus, setRestoreStatus] = useState(null);
  const [purgeStatus, setPurgeStatus] = useState(null);
  const errorMessage = databaseError ? (localizeErrorMessage(databaseError, t) ?? databaseError.message) : "";

  useAppShortcuts(createDatabaseErrorPageShortcuts({
    activeLanguage,
    handleLanguageChange
  }));

  async function handleRestoreFileSelection(file) {
    if (!file) {
      return;
    }

    try {
      setRestoreStatus({
        type: "pending",
        message: `${t("messages.backupSettings.reading")} ${file.name}...`
      });

      const text = await file.text();
      let payload;

      try {
        payload = JSON.parse(text);
      } catch (jsonError) {
        throw new Error(t("messages.backupSettings.backupFileNotValidJson"), { cause: jsonError });
      }

      setRestoreStatus({
        type: "pending",
        message: t("messages.backupSettings.restoringBackupIntoNewProfile")
      });

      await restoreProfileBackup(payload);

      setRestoreStatus({
        type: "success",
        message: t("messages.backupSettings.restored")
      });
      window.location.reload();
    } catch (restoreError) {
      setRestoreStatus({
        type: "error",
        message: localizeErrorMessage(restoreError, t) ?? t("messages.backupSettings.failedToRestoreBackup")
      });
    }
  }

  async function handlePurgeDatabase() {
    try {
      setPurgeStatus({
        type: "pending",
        message: t("messages.databaseSettings.deletingLocalDatabase")
      });
      await purgeWalletWatchDb();
      window.location.reload();
    } catch (purgeError) {
      setPurgeStatus({
        type: "error",
        message: localizeErrorMessage(purgeError, t) ?? t("messages.databaseSettings.failedToDeleteLocalDatabase")
      });
    }
  }

  return (
    <div class="viewport-guard" role="presentation">
      <div
        class="viewport-guard-card"
        role="alert"
        aria-live="polite"
        style={{ width: "min(100%, 56rem)" }}
      >
        <div class="viewport-guard-icon-wrap">
          <IconTriangleAlert className="viewport-guard-icon" />
        </div>
        <div class="database-error-lede" style={{ textAlign: "center" }}>
          <p class="viewport-guard-title">
            {t("messages.databaseErrorPage.title")}
          </p>
          <p class="viewport-guard-copy">
            {t("messages.databaseErrorPage.body")}
          </p>
          {errorMessage ? (
            <div class="viewport-guard-meta">
              <StatusMessage type="error" message={errorMessage} />
            </div>
          ) : null}
        </div>

        <div
          class="database-error-grid"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
            gap: "0.75rem",
            alignItems: "start",
            textAlign: "initial",
            width: "100%"
          }}
        >
          <Panel
            padded
            className="settings-pane--compact"
            header={t("restore")}
            footerClassName={footerToneClass(restoreStatus?.type)}
            footer={restoreStatus ? (
              <StatusMessage type={restoreStatus.type} message={restoreStatus.message} />
            ) : (
              t("messages.databaseErrorPage.restoreNote")
            )}
          >
            <div class="settings-backup-pane">
              <FileDropZone
                accept=".json,application/json"
                label={t("messages.backupSettings.dragBackupJsonHere")}
                note={t("messages.backupSettings.walletWatchBackupFileOnly")}
                onFileSelected={handleRestoreFileSelection}
              />
            </div>
          </Panel>

          <Panel
            padded
            className="settings-pane--compact"
            header={t("purgeDatabase")}
            footerClassName={footerToneClass(purgeStatus?.type)}
            footer={purgeStatus ? (
              <StatusMessage type={purgeStatus.type} message={purgeStatus.message} />
            ) : (
              t("messages.databaseSettings.deletesAllLocalProfilesBudgetsExpensesAndSettings")
            )}
          >
            <div class="settings-actions">
              <DestructiveActionButton onConfirm={handlePurgeDatabase}>
                {t("deleteEntireDatabase")}
              </DestructiveActionButton>
            </div>
          </Panel>
        </div>
      </div>
    </div>
  );
}
